import React, { memo, Fragment, useCallback } from 'react';
import { Col, Card, CardBody, Badge } from 'reactstrap';
import { FileResponse, FileId } from '@business/entities/file';
import { CommonService } from '@business/services';
import { OrgId } from '@business/entities/organization';
import { DealPresentationsBloc } from './DealPresentationsBloc';
import { useBloc } from '@core/utils/bloc';
import { useObservable } from '@core/utils/hooks/rxjs/useObservable';
import moment from 'moment';
import { AppConfig } from '@core/utils/config';
import { useCompany } from '@core/utils/hooks/misc';

export interface PresentationItemProps {
  presentation: FileResponse;
  onDelete(id: FileId): void;
  onClick(p: FileResponse): void;
}

const PresentationItem = memo((props: PresentationItemProps) => {
  const { presentation, onDelete, onClick } = props;

  return (
    <Col>
      <Card>
        <CardBody className="d-flex align-items-center">
          <div className="d-flex flex-row mt-2 mb-2 align-items-center">
            <div className="float-right ml-2" style={{ position: 'absolute', top: 10, right: 5 }}>
              <a
                className="text-danger"
                onClick={e => {
                  onDelete(presentation.id);
                  e.stopPropagation();
                }}
              >
                <i className="bx bx-trash font-size-16 text-danger"></i>
              </a>
            </div>
            <div style={{ position: 'relative' }}>
              <div
                style={{
                  width: 60,
                  height: 80,
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="bx bx-slideshow font-size-24 text-white"></i>
              </div>
              <Badge
                className="d-flex flex-row p-1 align-items-center text-center"
                style={{
                  position: 'absolute',
                  left: 0,
                  bottom: 0,
                  right: 0,
                  fontSize: 10,
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  borderBottomLeftRadius: 10,
                  borderBottomRightRadius: 10,
                  justifyContent: 'center',
                }}
                color={(presentation as any).isPublic || presentation.visibility === 'public' ? 'success' : 'secondary'}
              >
                {(presentation as any).isPublic || presentation.visibility === 'public' ? 'Public' : 'Private'}
              </Badge>
            </div>
            <div className="d-flex flex-column ml-4">
              <span className={`text-dark font-weight-medium font-size-16`} onClick={() => onClick(presentation)} style={{ cursor: 'pointer' }}>
                {presentation.name}
              </span>
              <span className="text-muted font-size-12">Created By: {CommonService.getFullName(presentation.user)}</span>
              <span className="text-muted font-size-12">Created: {moment.unix(presentation.created).format('MMM DD, YYYY')}</span>
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  );
});

export interface DealPresentationsProps {
  orgId: OrgId;
}

export const DealPresentations = memo((props: DealPresentationsProps) => {
  const { orgId } = props;
  const bloc = useBloc(DealPresentationsBloc, orgId);
  const items = useObservable(bloc.items$, []);
  const company = useCompany();

  const presentations = items.filter(item => item.type === 'file' && (item as FileResponse).fileType === 'presentation') as FileResponse[];
  const onClick = useCallback(
    (p: FileResponse) => {
      const appId = AppConfig.get().appId;
      window.open(`/presentation/${p.objectId}/preview/${appId}/${company.id}`);
    },
    [company.id],
  );

  return (
    <Fragment>
      {presentations.map(p => (
        <PresentationItem onClick={() => onClick(p)} presentation={p} key={p.id} onDelete={() => bloc.deleteFile(p.id).subscribe()} />
      ))}
    </Fragment>
  );
});
